/** @jsxImportSource @emotion/react */
import Input from 'components/atoms/Input'
import { formatRateAmount } from 'components/lab/payer/chargeRules/chargeRule'
import {
  HST_CODE_TIER_LABELS,
  HST_CODE_TIERS,
  HstTierPrice,
  HstTierPrices,
  hstTierCptCodes,
  hstTierTotal,
} from 'components/lab/payer/hstCodes/hstCodes'
import type { HstCodeComponent, HstCodeTier } from 'ts-back-end/src/business/payer/dto/payer.dto'

type Props = {
  prices: HstTierPrices
  onPriceChange: (
    tier: HstCodeTier,
    component: HstCodeComponent,
    field: keyof HstTierPrice,
    value: string,
  ) => void
  /** Distinguishes the inputs of each period. */
  name: string
}

const COLUMNS: [HstCodeComponent, keyof HstTierPrice, string][] = [
  ['TC', 'billed', 'TC Billed'],
  ['TC', 'rate', 'TC Rate'],
  ['PC', 'billed', 'PC Billed'],
  ['PC', 'rate', 'PC Rate'],
]

/** One tiered period's prices: TC and PC, billed and contracted, per tier. */
const HstTierGrid = ({ prices, onPriceChange, name }: Props) => (
  <table className="table table-sm mb-0" css={{ td: { verticalAlign: 'middle' } }}>
    <thead>
      <tr>
        <th>Tier</th>
        {COLUMNS.map(([, , label]) => (
          <th key={label}>{label}</th>
        ))}
        <th>Study Total</th>
      </tr>
    </thead>
    <tbody>
      {HST_CODE_TIERS.map((tier) => {
        const total = hstTierTotal(prices, tier, 'billed')
        const rate = hstTierTotal(prices, tier, 'rate')

        return (
          <tr key={tier}>
            <td>
              {HST_CODE_TIER_LABELS[tier]}
              <div css={{ fontSize: '0.75rem', color: '#6c757d' }}>{hstTierCptCodes(tier)}</div>
            </td>
            {COLUMNS.map(([component, field]) => (
              <td key={`${component}-${field}`}>
                <Input
                  type="number"
                  step="0.01"
                  value={prices[tier][component][field]}
                  placeholder={field === 'billed' ? 'Billed' : 'No rate'}
                  name={`${name}-${tier}-${component}-${field}`}
                  handleChange={(event) =>
                    onPriceChange(tier, component, field, event.target.value)
                  }
                />
              </td>
            ))}
            <td css={{ whiteSpace: 'nowrap' }}>
              <div className="font-weight-bold">
                {total === null ? '-' : formatRateAmount(total)}
              </div>
              <div css={{ fontSize: '0.75rem', color: '#6c757d' }}>
                {rate === null ? 'No contract rate' : `Rate ${formatRateAmount(rate)}`}
              </div>
            </td>
          </tr>
        )
      })}
    </tbody>
  </table>
)

export default HstTierGrid
